import type { NotificationChannelMatrixDto } from '@up2stay/up2promo-api-client';
import { toast } from 'sonner';

import {
  getPushPermissionStatus,
  requestPushPermissionAndRegister,
} from '@/core/lib/push-notifications';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

type EnsurePushPermissionLabels = {
  denied: string;
  unavailable: string;
};

export async function ensurePushPermissionBeforeEnable(
  labels: EnsurePushPermissionLabels,
): Promise<boolean> {
  if (!isCapacitorNative()) {
    toast.error(labels.unavailable);
    return false;
  }

  const status = await getPushPermissionStatus();
  if (status === 'denied') {
    toast.error(labels.denied);
    return false;
  }

  if (status === 'granted') {
    await requestPushPermissionAndRegister();
    return true;
  }

  const result = await requestPushPermissionAndRegister();
  if (!result.granted) {
    toast.error(labels.denied);
    return false;
  }

  return true;
}

export async function applyPushChannelChange(params: {
  previous: NotificationChannelMatrixDto;
  next: NotificationChannelMatrixDto;
  labels: EnsurePushPermissionLabels;
}): Promise<NotificationChannelMatrixDto> {
  if (!params.next.push || params.previous.push) {
    return params.next;
  }

  if (!isCapacitorNative()) {
    return params.next;
  }

  const allowed = await ensurePushPermissionBeforeEnable(params.labels);
  if (!allowed) {
    return params.previous;
  }

  return params.next;
}

/** Run once before bulk-enabling push for multiple types (native only). */
export async function ensurePushPermissionForBulkEnable(
  labels: EnsurePushPermissionLabels,
): Promise<boolean> {
  if (!isCapacitorNative()) {
    return true;
  }
  return ensurePushPermissionBeforeEnable(labels);
}
